import { Clause, LegalPage } from "@/pages/legal/LegalShell";
import { CANCELLATION_MATRIX, LEGAL_CONTACTS, ROLES } from "@/lib/legal";

export default function Terms() {
  return (
    <LegalPage
      title="Terms & Conditions"
      intro="These terms govern every booking made through CARENEST — the roles each person plays, when a provider's payout becomes guaranteed, and how cancellations are settled."
      testId="terms-page"
    >
      <Clause
        heading="1. User roles, accountability & guarantor relationships"
        paras={["The platform recognises five distinct user entities, each bound by specific financial and legal obligations."]}
        testId="terms-roles"
      >
        <dl className="mt-5 space-y-4">
          {ROLES.map((r) => (
            <div key={r.name} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`terms-role-${r.name.toLowerCase().replaceAll(" ", "-")}`}>
              <dt className="font-display text-base font-bold text-slate-900">{r.name}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-slate-600">{r.body}</dd>
            </div>
          ))}
        </dl>
      </Clause>

      <Clause
        heading="2. The “Arrival = Guaranteed Payout” non-negotiable rule"
        paras={[
          "To protect clinical professionals devoting clinical time, equipment and transportation resources in Nigerian urban centres, CARENEST enforces an absolute Arrival = Guaranteed Payout policy.",
          "The exact moment a Service Provider physically arrives at the client's location and the on-site Witness reads the valid Termii SMS OTP to the provider — who inputs it into the application to reach the VERIFIED_ARRIVAL_PAYOUT_SECURED state — the provider's 97% disbursement is legally guaranteed and non-refundable.",
          "Witness as legal guarantor — by populating the Witness Directory Matrix, the Master Client explicitly delegates on-site supervisory authority to the designated Witness. Entering the arrival PIN constitutes irreversible confirmation of provider arrival and site access authorisation.",
          "Post-arrival clinical quality claims — any dispute lodged within the 24-hour dispute window regarding clinical performance, demeanour or care standards is handled as an administrative grievance. Post-arrival disputes do not invalidate or reverse the provider's arrival payout guarantee unless fraudulent collusion or physical non-attendance is proven via audit logs.",
        ]}
        testId="terms-arrival"
      />

      <Clause
        heading="3. Cancellation & refund matrix"
        paras={["All appointment cancellations and refund allocations are governed strictly by time parameters and doorstep verification triggers."]}
        testId="terms-cancellation"
      >
        <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-left text-sm" data-testid="cancellation-matrix">
            <thead>
              <tr className="border-b border-slate-200 bg-stone-50">
                {CANCELLATION_MATRIX.head.map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CANCELLATION_MATRIX.rows.map((row) => (
                <tr key={row[0]} className="border-b border-slate-100 last:border-0">
                  {row.map((cell, i) => (
                    <td key={i} className={`px-4 py-3 align-top ${i === 0 ? "font-medium text-slate-900" : "text-slate-600"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Clause>

      <Clause
        heading="4. Disclaimers & intermediary status"
        paras={[
          "CARENEST is a specialised technology marketplace platform facilitating connections between independent health professionals and clients. CARENEST is not a direct healthcare provider, hospital, or employer of listed practitioners.",
          "The platform explicitly disclaims liability for direct medical outcomes, medical malpractice, or emergency intervention. CARENEST services are strictly intended for pre-scheduled, in-home healthcare and rehabilitation services.",
          `Legal notices may be sent to ${LEGAL_CONTACTS.legal}.`,
        ]}
        testId="terms-disclaimer"
      />
    </LegalPage>
  );
}
